import { useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import type { IconName } from "@/components/common/Icon";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EnvironmentChatList, type EnvironmentChatsEmptyHint } from "./EnvironmentChatList";
import { EnvironmentPowerButton } from "./EnvironmentPowerButton";
import { CARD_ACTION_ICON_SIZE, CARD_ACTION_SIZE, cardActionColor } from "./card-action-style";
import {
	MetaSeparator,
	ResourceCard,
	ResourceRow,
	ResourceTag,
	ResourceTitle,
} from "@/components/common/ResourceCard";
import { EnvironmentStatusBadge, type EnvironmentStatusLoadState } from "./EnvironmentStatusBadge";
import { EnvironmentErrorDialog } from "./EnvironmentErrorDialog";
import { EnvironmentSettingsDialog } from "./EnvironmentSettingsDialog";
import {
	EnvironmentInstanceSizeDialog,
	type EnvironmentStartChoice,
} from "./EnvironmentInstanceSizeDialog";
import { EnvironmentLocationBadge } from "./EnvironmentLocationBadge";
import {
	ApiStatusError,
	getEnvironmentStatus,
	deleteEnvironment,
	environmentHasPod,
	formatEnvironmentSize,
	restartEnvironment,
	scanDeviceSharedVolumes,
	startEnvironment,
	stopEnvironment,
	type Environment,
	type EnvironmentStatus,
	type EnvironmentStatusEntry,
	type SandboxErrorReason,
} from "@/lib/api";
import { isAbortError } from "@/lib/abort-error";
import {
	ensureEnvironmentRunning,
	pollEnvironmentUntil,
	waitForEnvironmentRestart,
} from "@/lib/environment-lifecycle";
import { summarizeScan } from "@/lib/device-shared-volumes";
import { showEnvironmentStopNotice } from "@/lib/environment-stop-notice";
import { formatDateTime } from "@/lib/format-datetime";
import { canOpenPluginMarketplace } from "@/lib/marketplace";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { useChatStore } from "@/stores/useChatStore";
import { useDeviceSharedVolumeStore } from "@/stores/useDeviceSharedVolumeStore";
import { useToastStore } from "@/stores/useToastStore";
import { useHasPermission } from "@/lib/permissions";
import { canSeeRunnerManagement } from "@/lib/runner-access";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";

type EnvironmentOperation = "start" | "stop" | "restart" | "delete";

const WAIT_FOR_TRANSITION = "エージェント環境の状態が切り替わるまでお待ちください。";

/** 状態が分からず操作できない理由。取得に失敗したのか、まだ取得中なのかで出し分ける。 */
function unknownStatusReason(statusState: EnvironmentStatusLoadState): string {
	return statusState === "error"
		? "エージェント環境の状態を取得できませんでした。"
		: "エージェント環境の状態を確認しています。";
}

/** 新規チャットが押せない理由。押せるようにするために何をすればよいかを示す。押せるときは null。 */
function newChatDisabledReason({
	status,
	statusState,
	operationPending,
	startDisabledReason,
}: {
	status: EnvironmentStatus | undefined;
	statusState: EnvironmentStatusLoadState;
	operationPending: boolean;
	startDisabledReason: string | null;
}): string | null {
	if (operationPending) return WAIT_FOR_TRANSITION;
	if (status === "running") return null;
	if (startDisabledReason !== null) return startDisabledReason;
	if (status === undefined) return unknownStatusReason(statusState);
	if (status === "errored") return "エージェント環境に異常があります。再起動してください。";
	if (!(status === "notRunning" || status === "startupFailed")) return WAIT_FOR_TRANSITION;
	return "エージェント環境を起動すると新規チャットを始められます。";
}

/** ヘッダーに並べる操作のアイコンボタン。状態で変わる説明・無効の理由をツールチップで示す。 */
function CardActionButton({
	icon,
	tooltip,
	disabled,
	busy = false,
	testId,
	onClick,
}: {
	icon: IconName;
	tooltip: string;
	disabled: boolean;
	busy?: boolean;
	testId: string;
	onClick: () => void;
}) {
	// 無効なボタンはホバーを受けないため、ツールチップは外側の要素に付ける。
	return (
		<span data-tooltip={tooltip} style={{ display: "inline-flex" }}>
			<IconButton
				icon={icon}
				size={CARD_ACTION_SIZE}
				iconSize={CARD_ACTION_ICON_SIZE}
				disabled={disabled}
				busy={busy}
				data-testid={testId}
				color={cardActionColor(disabled && !busy)}
				onClick={onClick}
			/>
		</span>
	);
}

/** ヘッダーに並べる文字付きの操作ボタン。 */
function CardActionTextButton({
	icon,
	label,
	disabledReason,
	testId,
	onClick,
}: {
	icon: IconName;
	label: string;
	disabledReason: string | null;
	testId: string;
	onClick: () => void;
}) {
	// ConfirmButton は押せない理由を持たないため、外側の要素に付ける。
	return (
		<span
			data-tooltip={disabledReason ?? undefined}
			style={{ display: "inline-flex", marginRight: 6 }}
		>
			<ConfirmButton
				variant="outline"
				size="sm"
				icon={icon}
				label={label}
				loading={false}
				disabled={disabledReason !== null}
				data-testid={testId}
				onClick={onClick}
			/>
		</span>
	);
}

const DELETE_MESSAGE = "削除したら、エージェント環境内のファイルはすべて削除されます。";

const STOP_PUSH_NOTE =
	"コピーなので、反映すると共有ボリュームのファイルが上書きされます。反映しないと、変更はこの環境の写しに残ります (後で起動してから、ファイルのツリーの右クリックで反映できます)。";

/** 停止の確認を開いたときに数え直した、共有ボリュームに反映していない変更。 */
type PodStopVolumes =
	| { status: "loading" }
	| {
			status: "ready";
			unpushed: number;
			/** 数え直せた (false なら最後に確かめたときの件数)。 */
			counted: boolean;
			/** ランナーが停止の後の反映に対応している。 */
			supported: boolean;
			uncountedMessage: string | null;
	  }
	| { status: "failed"; message: string };

/** 停止の確認に添える、反映していない変更の文。 */
function describePodStopVolumes(volumes: PodStopVolumes): string {
	switch (volumes.status) {
		case "loading":
			return "共有ボリュームに反映していない変更を確かめています…";
		case "failed":
			return `共有ボリュームに反映していない変更を確かめられませんでした (${volumes.message})。`;
		case "ready": {
			const count = volumes.counted
				? `反映していない変更: ${volumes.unpushed} 件`
				: `最後に確かめたときの反映していない変更: ${volumes.unpushed} 件`;
			return volumes.uncountedMessage === null ? count : `${volumes.uncountedMessage}${count}`;
		}
	}
}

/** padding 込みのカード幅。チャット一覧が窮屈にならない幅にする。 */
const ENVIRONMENT_CARD_WIDTH = 700;

/** カードを並べる grid の列。広い画面ではカード幅の列を並べ、カード幅に満たない画面では 1 列で幅いっぱいに縮める。 */
export const ENVIRONMENT_CARD_GRID_COLUMNS = `repeat(auto-fill, minmax(min(100%, ${ENVIRONMENT_CARD_WIDTH}px), ${ENVIRONMENT_CARD_WIDTH}px))`;

/**
 * 仮想環境 1 件のカード。名前・状態とタイプのバッジ・チャット一覧を表示し、
 * 右上のボタンから新規チャット・起動/停止・再起動を操作し、隣の歯車ボタンから設定を開ける。
 * 管理コンソールとプロジェクトページの両方で同じ見た目・機能になるよう共通化している。
 * 名前変更・設定変更後は `onUpdated`、起動・停止要求後は `onStatusChanged`、削除後は `onDeleted` で
 * 呼び出し側の一覧へ反映する。
 * `startDisabledReason` が非 null なら状態に依らず起動を無効にし、理由をツールチップで示す
 * (組織の同時稼働上限に達している、プロジェクトがアーカイブ済み、クレジット枯渇など)。
 * 停止は無効にしない (アーカイブ済みでも稼働中の環境は止められるようにする)。
 * デバイスの環境は実行場所のバッジを出す。オフラインの印は状態の取得 (`runnerOnline`) で更新し、無ければ一覧を取った時点の値。
 * 起動は無効にせず、オフラインのデバイスへの起動は backend が理由付きで断る。インスタンスサイズはクラウドと同じく起動の
 * ときに選ぶ。
 */
export function EnvironmentCard({
	environment,
	status,
	statusState,
	errorReason,
	failureDetail,
	runnerOnline,
	scheduledRestartAt,
	startDisabledReason = null,
	onUpdated,
	onStatusChanged,
	onDeleted,
	onNewChat,
}: {
	environment: Environment;
	status: EnvironmentStatus | undefined;
	statusState: EnvironmentStatusLoadState;
	errorReason?: SandboxErrorReason;
	/** 起動の失敗の具体的な説明 (状態の取得の failureDetail)。 */
	failureDetail?: string | null;
	/** 状態の取得で分かったデバイスのオンライン。無ければ一覧の値を使う。 */
	runnerOnline?: boolean;
	/** サーバーの保守で再起動される期限 (状態の取得の scheduledRestartAt)。 */
	scheduledRestartAt?: string | null;
	startDisabledReason?: string | null;
	onUpdated?: (environment: Environment) => void;
	/** 状態が変わったときに、取得した状態の全項目を渡す (起動・停止の要求直後は environmentId と status だけ)。 */
	onStatusChanged?: (entry: EnvironmentStatusEntry) => void;
	onDeleted?: (environmentId: string) => void;
	/**
	 * 新規チャットを始める。渡したときだけチャット一覧を出す (管理コンソールは他組織のエージェントも並び、
	 * 自分のチャット一覧と結びつかないため渡さない)。
	 */
	onNewChat?: (environment: Environment) => void;
}) {
	const canManageAgents = useHasPermission("manageAgents");
	const canUpdateDevice = useCurrentUserStore((s) =>
		canSeeRunnerManagement(s.role, s.deviceManagementAvailable),
	);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [errorDialogOpen, setErrorDialogOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deleteMessage, setDeleteMessage] = useState(DELETE_MESSAGE);
	const [deletePreparing, setDeletePreparing] = useState(false);
	const [startOpen, setStartOpen] = useState(false);
	const [confirmingPodStop, setConfirmingPodStop] = useState<"stop" | "restart" | null>(null);
	const [podStopVolumes, setPodStopVolumes] = useState<PodStopVolumes>({ status: "loading" });
	const hasDeviceVolumes = environment.runner !== null && environment.volumeIds.length > 0;
	const settingsButtonRef = useRef<HTMLButtonElement>(null);
	/** 削除の確認を開く要求の通し番号。設定を閉じたら・カードが消えたら進め、数え直しの後に開くのを取りやめる。 */
	const deleteRequestRef = useRef(0);
	useEffect(() => {
		const requests = deleteRequestRef;
		return () => {
			requests.current++;
		};
	}, []);
	// 停止の確認を開いたら、裏で共有ボリュームに反映していない変更を数え直す。
	const checksPodStopVolumes = confirmingPodStop === "stop" && hasDeviceVolumes;
	useEffect(() => {
		if (!checksPodStopVolumes) return;
		let cancelled = false;
		setPodStopVolumes({ status: "loading" });
		reportSharedVolumeFailure(
			scanDeviceSharedVolumes(environment.id).then(
				(scanned) => {
					if (cancelled) return;
					setPodStopVolumes({
						status: "ready",
						...summarizeScan(scanned),
						supported: scanned.device.runnerSupported,
					});
				},
				(err: unknown) => {
					if (!cancelled) {
						setPodStopVolumes({
							status: "failed",
							message: err instanceof Error ? err.message : "不明なエラー",
						});
					}
					throw err;
				},
			),
			"停止の前に共有ボリュームの変更を数え直せませんでした。",
		);
		return () => {
			cancelled = true;
		};
	}, [checksPodStopVolumes, environment.id]);
	// 再起動では数え直さず、ファイルのツリーで最後に取った件数で注記する。
	const lastKnownUnpushed = useDeviceSharedVolumeStore((s) =>
		s.environmentId === environment.id && s.overview !== null
			? summarizeScan(s.overview).unpushed
			: 0,
	);
	// ネットワークタブは停止中でも使えるため、ダイアログ自体は常に開ける。プラグイン系タブの可否だけ渡す。
	const pluginMarketplaceAvailable = canOpenPluginMarketplace(status);
	// サイズは Pod の属性なので、Pod がある状態でだけ意味を持つ。停止中に保存値を出すと
	// 「今そのサイズで課金されている」と誤読されるため、Pod が無い間はタイプのバッジを出さない。
	const hasPod = environmentHasPod(status);
	const runner =
		environment.runner && runnerOnline !== undefined
			? { ...environment.runner, online: runnerOnline }
			: environment.runner;

	const [pendingOperation, setPendingOperation] = useState<EnvironmentOperation | null>(null);
	const operationPending = pendingOperation !== null;
	const operationControllerRef = useRef<AbortController | null>(null);
	useEffect(() => {
		const controllerRef = operationControllerRef;
		return () => controllerRef.current?.abort();
	}, []);

	const runEnvironmentOperation = async (
		kind: EnvironmentOperation,
		fallbackMessage: string,
		operation: (controller: AbortController) => Promise<unknown>,
	) => {
		if (operationControllerRef.current) return;
		const controller = new AbortController();
		operationControllerRef.current = controller;
		setPendingOperation(kind);
		try {
			await operation(controller);
		} catch (err) {
			if (isAbortError(err)) return;
			// 呼び出し元はメニューやダイアログの確定で、戻りの Promise を受けない。ここでトースト表示
			// まで済ませて完結させる (再 throw すると未処理の Promise 拒否になるだけで誰にも届かない)。
			useToastStore.getState().show({
				type: "error",
				message: err instanceof Error ? err.message : fallbackMessage,
			});
			if (err instanceof ApiStatusError && err.status === 409 && !controller.signal.aborted) {
				try {
					const entry = await getEnvironmentStatus(environment.id, controller.signal);
					if (!controller.signal.aborted) onStatusChanged?.(entry);
				} catch (refreshError) {
					if (!isAbortError(refreshError))
						useToastStore.getState().show({
							type: "error",
							message:
								"エージェントの現在の状態を確認できませんでした。ページを再読み込みしてください。",
						});
				}
			}
		} finally {
			if (operationControllerRef.current === controller) operationControllerRef.current = null;
			if (!controller.signal.aborted) setPendingOperation(null);
		}
	};

	// 起動はサイズ選択ダイアログを経る。選んだサイズ (デバイスでは GPU を使うかも) はサーバーが起動要求の受理と同時に環境へ保存する
	// ので、一覧側の表示 (再起動・次回の起動の既定) は要求が受理された後にだけ更新する。先に更新すると、
	// 同時稼働上限などで 409 になったときに保存されていないサイズが表示に残る。
	const handleStart = ({ size: instanceSize, gpu }: EnvironmentStartChoice) => {
		setStartOpen(false);
		return runEnvironmentOperation("start", "エージェントの起動に失敗しました。", (controller) =>
			ensureEnvironmentRunning(environment.id, {
				initialStatus: status
					? { environmentId: environment.id, status, errorReason, failureDetail }
					: undefined,
				instanceSize,
				gpu,
				start: async (environmentId, options) => {
					await startEnvironment(environmentId, options);
					if (
						instanceSize !== environment.instanceSize ||
						(gpu !== undefined && gpu !== environment.gpu)
					) {
						onUpdated?.({ ...environment, instanceSize, gpu: gpu ?? environment.gpu });
					}
				},
				signal: controller.signal,
				onStatusChanged,
			}),
		);
	};

	// 停止 → 終了完了待ち → 起動はサーバー側で完結するため、ここは要求と進捗のポーリングのみ。
	// ページを離れても再起動自体は完遂される (ポーリングが止まるだけ)。
	const handleRestart = () => {
		setConfirmingPodStop(null);
		return runEnvironmentOperation(
			"restart",
			"エージェントの再起動に失敗しました。",
			async (controller) => {
				const result = await restartEnvironment(environment.id);
				showEnvironmentStopNotice(result);
				if (controller.signal.aborted) return;
				onStatusChanged?.({ environmentId: environment.id, status: "shuttingDown" });
				await waitForEnvironmentRestart(environment.id, {
					signal: controller.signal,
					onStatusChanged,
				});
			},
		);
	};

	// pushSharedVolumes は共有ボリュームが付いたデバイスの環境で、反映するかを選んだときだけ渡す。
	const handleStop = (pushSharedVolumes?: boolean) => {
		setConfirmingPodStop(null);
		// 確保待ちの間に停止したら、起動の完了待ちを打ち切ってから止める。
		if (pendingOperation === "start") {
			operationControllerRef.current?.abort();
			operationControllerRef.current = null;
		}
		return runEnvironmentOperation(
			"stop",
			"エージェントの停止に失敗しました。",
			async (controller) => {
				const result = await stopEnvironment(
					environment.id,
					pushSharedVolumes === undefined ? {} : { pushSharedVolumes },
				);
				showEnvironmentStopNotice(result);
				if (result?.sharedVolumesPushRequested) {
					useToastStore
						.getState()
						.show({ type: "info", message: "共有ボリュームへの反映を始めました。" });
				} else if (pushSharedVolumes) {
					// 付いているボリュームが無いかランナーが古いと、backend は反映を外して止める。
					useToastStore.getState().show({
						type: "error",
						message:
							"共有ボリュームへの反映は行われませんでした。デバイスのランナーの更新が必要な可能性があります。",
					});
				}
				if (controller.signal.aborted) return;
				onStatusChanged?.({ environmentId: environment.id, status: "shuttingDown" });
				await pollEnvironmentUntil(
					environment.id,
					(nextStatus) => nextStatus === "notRunning" || nextStatus === "errored",
					{
						signal: controller.signal,
						transitionalStatus: "shuttingDown",
						onStatusChanged,
					},
				);
			},
		);
	};

	// デバイスの環境に共有ボリュームが付いていれば、手元の写しの反映していない変更を数え直してから確認を開く。
	// 数えられなくても削除は選べるよう、理由を添えて開く。数え直しの間に設定を閉じたら開かない。
	const requestDelete = async () => {
		const request = ++deleteRequestRef.current;
		const open = (message: string) => {
			if (deleteRequestRef.current !== request) return;
			setDeleteMessage(message);
			setDeleteOpen(true);
		};
		if (environment.runner === null || environment.volumeIds.length === 0) {
			open(DELETE_MESSAGE);
			return;
		}
		setDeletePreparing(true);
		try {
			// 命令を送れない (オフラインなど) ときは数え直せず、保存済みの件数が返る。
			const { unpushed, counted, uncountedMessage } = summarizeScan(
				await scanDeviceSharedVolumes(environment.id),
			);
			open(
				[
					DELETE_MESSAGE,
					uncountedMessage ?? "",
					unpushed === 0
						? ""
						: counted
							? `共有ボリュームに反映していない変更が ${unpushed} 件あります。残すには、起動してからファイルのツリーで共有ボリュームの行を右クリックして反映してください。`
							: `最後に確かめたときは、共有ボリュームに反映していない変更が ${unpushed} 件ありました。`,
				].join(""),
			);
		} catch (err) {
			open(
				`${DELETE_MESSAGE}共有ボリュームに反映していない変更があるかを確かめられませんでした (${err instanceof Error ? err.message : "不明なエラー"})。`,
			);
			throw err;
		} finally {
			setDeletePreparing(false);
		}
	};

	// 削除はサーバー側で停止を伴って同期的に確定する。失敗は ConfirmDialog がダイアログ内に表示する。
	// チャットはサーバー側でアーカイブ済みの孤児 (environmentId = null) になるため、
	// ローカルのチャット一覧と開いているチャットの状態も同じ形に揃える。
	const handleDeleteConfirm = async () => {
		if (operationControllerRef.current) return;
		const controller = new AbortController();
		operationControllerRef.current = controller;
		setPendingOperation("delete");
		try {
			await deleteEnvironment(environment.id);
			setDeleteOpen(false);
			onDeleted?.(environment.id);
			useChatStore.getState().markEnvironmentDeleted(environment.id);
			useToastStore.getState().show({
				type: "success",
				message: `エージェント「${environment.name}」を削除しました。`,
			});
		} finally {
			operationControllerRef.current = null;
			if (!controller.signal.aborted) setPendingOperation(null);
		}
	};

	// Pod が無い状態 (休止中・起動失敗) で起動を、Pod がある状態 (稼働中・異常) で停止を許可する。
	// 異常時の起動は既存 Pod を作り直さず無効なため、停止で Pod を消してから起動し直す。一方、起動失敗
	// (reaper 回収済み) は Pod が既に無く、起動が失敗記録を解除して作り直すためそのまま起動できる。
	// 遷移中 (起動準備中・終了処理中) と状態不明時は誤操作を避けるためどちらも無効にする。
	// インスタンス確保待ちは無期限に続くため、停止 (= 待機の取り消し) だけは許可する。このカードから
	// 起動して完了を待っている最中でも許可する。
	// 同時稼働上限に達している場合も起動を無効にする (理由は呼び出し側が一覧の脇に表示する)。
	const canStart =
		!operationPending &&
		(status === "notRunning" || status === "startupFailed") &&
		startDisabledReason === null;
	const canStop =
		(!operationPending && (status === "running" || status === "errored")) ||
		((!operationPending || pendingOperation === "start") && status === "waitingForCapacity");
	// 停止と同じく Pod がある状態でのみ許可する。再起動は起動を伴うため、起動が
	// 無効な状況 (アーカイブ済みなど) では同様に無効にする。
	const canRestart =
		!operationPending &&
		(status === "running" || status === "errored") &&
		startDisabledReason === null;
	// 確認中に別経路で止まるなどして操作できなくなったら、確認ダイアログを閉じる。
	if (confirmingPodStop !== null && !(confirmingPodStop === "stop" ? canStop : canRestart)) {
		setConfirmingPodStop(null);
	}
	// 共有ボリュームが付いたデバイスの環境の停止では、反映するかを選ばせる (ランナーが古いと分かれば選ばせない)。
	const choosesPush =
		confirmingPodStop === "stop" &&
		hasDeviceVolumes &&
		!(podStopVolumes.status === "ready" && !podStopVolumes.supported);
	const showsRestartNote =
		confirmingPodStop === "restart" && hasDeviceVolumes && lastKnownUnpushed > 0;
	// 停止と再起動はどちらも Pod を止めるため、同じ確認ダイアログで同じ影響を伝える。
	const podStopDialog =
		confirmingPodStop === "restart"
			? { title: "エージェントを再起動しますか？", label: "再起動", onConfirm: handleRestart }
			: choosesPush
				? {
						title: "エージェントを停止しますか？",
						label: "反映して停止",
						onConfirm: () => handleStop(true),
					}
				: { title: "エージェントを停止しますか？", label: "停止", onConfirm: () => handleStop() };
	const startTooltip = canStart
		? "起動"
		: (startDisabledReason ??
			(status === undefined ? unknownStatusReason(statusState) : WAIT_FOR_TRANSITION));
	const newChatBlockedReason = newChatDisabledReason({
		status,
		statusState,
		operationPending,
		startDisabledReason,
	});
	// 案内は押せるボタンだけを指す。押せるボタンが無ければ、新規チャットが押せない理由を示す。
	const emptyChatsHint: EnvironmentChatsEmptyHint =
		newChatBlockedReason === null
			? { text: "カード右上の「新規チャット」から始められます。" }
			: canStart
				? { icon: "play", text: "でエージェント環境を起動すると、チャットを始められます。" }
				: status === "errored" && canRestart
					? { icon: "restart", text: "でエージェント環境を再起動してください。" }
					: { text: newChatBlockedReason };
	const restartTooltip = canRestart
		? "再起動"
		: (startDisabledReason ??
			(status === undefined
				? unknownStatusReason(statusState)
				: status === "notRunning" || status === "startupFailed"
					? "停止中のエージェントは再起動できません。"
					: WAIT_FOR_TRANSITION));

	return (
		<ResourceCard testId={`environment-card-${environment.id}`}>
			<ResourceRow
				icon="agent"
				title={<ResourceTitle>{environment.name}</ResourceTitle>}
				tags={
					<>
						<EnvironmentLocationBadge runner={runner} canUpdateDevice={canUpdateDevice} />
						{hasPod && (
							<ResourceTag testId={`environment-instance-size-${environment.id}`}>
								{formatEnvironmentSize(environment)}
							</ResourceTag>
						)}
					</>
				}
				meta={
					<>
						<EnvironmentStatusBadge status={status} state={statusState} />
						{(status === "errored" || status === "startupFailed") && (
							<>
								<MetaSeparator />
								<button
									type="button"
									data-testid={`environment-error-info-${environment.id}`}
									onClick={() => setErrorDialogOpen(true)}
									style={{
										padding: 0,
										border: 0,
										background: "transparent",
										fontFamily: "inherit",
										fontSize: "var(--text-caption)",
										fontWeight: 500,
										color: "var(--accent)",
										cursor: "pointer",
									}}
								>
									詳細
								</button>
							</>
						)}
					</>
				}
				actions={
					<>
						{onNewChat && (
							<CardActionTextButton
								icon="plus"
								label="新規チャット"
								disabledReason={newChatBlockedReason}
								testId={`environment-new-chat-${environment.id}`}
								onClick={() => onNewChat(environment)}
							/>
						)}
						{canManageAgents && (
							<>
								<EnvironmentPowerButton
									action={hasPod ? "stop" : "start"}
									tooltip={hasPod ? (canStop ? "停止" : WAIT_FOR_TRANSITION) : startTooltip}
									disabled={hasPod ? !canStop : !canStart}
									busy={(pendingOperation === "start" && !canStop) || pendingOperation === "stop"}
									testId={`environment-${hasPod ? "stop" : "start"}-${environment.id}`}
									onClick={hasPod ? () => setConfirmingPodStop("stop") : () => setStartOpen(true)}
								/>
								<CardActionButton
									icon="restart"
									tooltip={restartTooltip}
									disabled={!canRestart}
									busy={pendingOperation === "restart"}
									testId={`environment-restart-${environment.id}`}
									onClick={() => setConfirmingPodStop("restart")}
								/>
							</>
						)}
						<IconButton
							ref={settingsButtonRef}
							icon="settings"
							size={CARD_ACTION_SIZE}
							iconSize={CARD_ACTION_ICON_SIZE}
							color={cardActionColor(false)}
							data-testid={`environment-settings-${environment.id}`}
							onClick={() => setSettingsOpen(true)}
						/>
					</>
				}
			/>
			{scheduledRestartAt && (
				<p
					className="m-0 text-caption text-warning"
					style={{ padding: "0 16px 12px" }}
					data-testid={`environment-scheduled-restart-${environment.id}`}
				>
					サーバーの保守のため、{formatDateTime(scheduledRestartAt)}
					に再起動されます。{canManageAgents && "それより前に ↻ で再起動することもできます。"}
					ファイルは残ります。
				</p>
			)}
			{onNewChat && (
				<div style={{ padding: "0 16px 16px" }}>
					<EnvironmentChatList environmentId={environment.id} emptyHint={emptyChatsHint} />
				</div>
			)}
			<EnvironmentInstanceSizeDialog
				open={startOpen}
				initialSize={environment.instanceSize}
				initialGpu={environment.gpu}
				onClose={() => setStartOpen(false)}
				onConfirm={(choice) => void handleStart(choice)}
				runner={runner}
			/>
			<EnvironmentErrorDialog
				open={errorDialogOpen}
				errorReason={errorReason}
				failureDetail={failureDetail}
				onClose={() => setErrorDialogOpen(false)}
			/>
			<EnvironmentSettingsDialog
				open={settingsOpen}
				environment={environment}
				pluginsAvailable={pluginMarketplaceAvailable}
				terminalAvailable={status === "running"}
				onDelete={() =>
					reportSharedVolumeFailure(
						requestDelete(),
						"削除の前に共有ボリュームの変更を確かめられませんでした。",
					)
				}
				deleteDisabled={operationPending}
				deleteLoading={deletePreparing}
				podExists={hasPod}
				onClose={() => {
					deleteRequestRef.current++;
					setSettingsOpen(false);
				}}
				onEnvironmentUpdated={(updated) => onUpdated?.(updated)}
				anchor={settingsButtonRef.current}
			/>
			<ConfirmDialog
				open={confirmingPodStop !== null}
				title={podStopDialog.title}
				message={
					status === "running"
						? "実行中のチャットは中断されます。エージェント環境内のファイルは残ります。"
						: "エージェント環境内のファイルは残ります。"
				}
				confirmLabel={podStopDialog.label}
				confirmVariant="primary"
				alternative={
					choosesPush
						? { label: "反映せずに停止", onConfirm: async () => void handleStop(false) }
						: undefined
				}
				onConfirm={async () => void podStopDialog.onConfirm()}
				onCancel={() => setConfirmingPodStop(null)}
			>
				{(checksPodStopVolumes || showsRestartNote) && (
					<div className="mt-2 flex flex-col gap-1.5 text-caption leading-relaxed text-fg-2">
						{checksPodStopVolumes && (
							<p className="m-0" data-testid="pod-stop-unpushed">
								{describePodStopVolumes(podStopVolumes)}
							</p>
						)}
						{choosesPush && (
							<p className="m-0" data-testid="pod-stop-push-note">
								{STOP_PUSH_NOTE}
							</p>
						)}
						{showsRestartNote && (
							<p className="m-0" data-testid="pod-restart-push-note">
								再起動では共有ボリュームに反映しません。
							</p>
						)}
					</div>
				)}
			</ConfirmDialog>
			<ConfirmDialog
				open={deleteOpen}
				title="ご注意！"
				message={deleteMessage}
				confirmLabel="削除"
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteOpen(false)}
			/>
		</ResourceCard>
	);
}
